import styled from 'styled-components';

export const PageWrapper = styled.div`
  background: #1a1815;
  min-height: 100vh;
  height: 100vh;
  padding-top: 88px;
  color: #f3ede4;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const Main = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const Wrap = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding-inline: clamp(20px, 4.5vw, 64px);
`;

export const HeroSection = styled(Wrap)`
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 0.8fr);
  gap: 48px;
  align-items: center;
  flex: 1;
  padding-block: 0;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    gap: 24px;
    padding-block: 24px 40px;
    overflow-y: auto;
  }
`;

export const CopyColumn = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 22px;

  @media (max-width: 860px) {
    gap: 18px;
  }
`;

export const Eyebrow = styled.div`
  font-family: 'JetBrains Mono', 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  font-size: 12px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: #d0583c;
  display: flex;
  align-items: center;
  gap: 10px;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #d0583c;
    animation: pulse 2s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(0.8); }
  }
`;

export const HeroTitle = styled.h1`
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-weight: 400;
  font-size: clamp(44px, 5.5vw, 82px);
  line-height: 0.95;
  letter-spacing: -0.02em;
  margin: 0;
  text-wrap: balance;

  em {
    font-style: italic;
    color: #d0583c;
  }
`;

export const HeroSubtitle = styled.p`
  font-family: 'Newsreader', Georgia, serif;
  font-size: 19px;
  line-height: 1.55;
  color: #a89f93;
  max-width: 34em;
  margin: 0;

  @media (max-width: 480px) {
    font-size: 17px;
  }
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 500px;
  margin-top: 4px;
`;

export const FormRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

export const Input = styled.input`
  flex: 1 1 160px;
  min-width: 0;
  height: 48px;
  background: #23201b;
  border: 1px solid #34302a;
  color: #f3ede4;
  font-family: 'Newsreader', Georgia, serif;
  font-size: 16px;
  padding: 0 16px;
  border-radius: 2px;

  &::placeholder {
    color: #7d756b;
  }

  &:focus {
    outline: none;
    border-color: #d0583c;
  }
`;

export const SubmitButton = styled.button<{ $isDisabled?: boolean }>`
  background: ${props => props.$isDisabled ? '#5a534a' : '#d0583c'};
  color: #fff;
  border: 0;
  font-family: 'Newsreader', Georgia, serif;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0.01em;
  padding: 16px 22px;
  border-radius: 2px;
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: #b94b31;
  }

  &:focus-visible {
    outline: 2px solid #f3ede4;
    outline-offset: 3px;
  }
`;

export const ErrorMessage = styled.p<{ $hidden?: boolean }>`
  color: #f0997b;
  font-size: 14px;
  margin: 0;
  display: ${props => props.$hidden ? 'none' : 'block'};
`;

export const FineText = styled.p`
  font-size: 14px;
  color: #a89f93;
  margin: 0;

  strong {
    color: #f3ede4;
    font-weight: 500;
  }
`;

export const SuccessMessage = styled.p`
  color: #f3ede4;
  font-family: 'Newsreader', Georgia, serif;
  font-size: 18px;
  margin: 0;
`;

export const ProofBar = styled.div`
  display: flex;
  gap: 28px;
  flex-wrap: wrap;
  padding-top: 22px;
  border-top: 1px solid #34302a;
  font-family: 'JetBrains Mono', 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  font-size: 11.5px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #a89f93;

  b {
    color: #f3ede4;
    font-weight: 500;
  }
`;

export const StageColumn = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;

  @media (max-width: 860px) {
    order: -1;
    gap: 12px;
  }
`;

export const PhoneMockup = styled.div`
  position: relative;
  height: min(70vh, 480px);
  aspect-ratio: 9 / 19.5;
  max-width: 100%;
  border-radius: 40px;
  padding: 10px;
  background: #0d0c0a;
  border: 1px solid #34302a;
  box-shadow: 0 40px 80px -30px rgba(0,0,0,0.7);

  &::before {
    content: '';
    position: absolute;
    top: 18px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 24px;
    border-radius: 16px;
    background: #0d0c0a;
    z-index: 2;
  }

  @media (max-width: 860px) {
    height: min(45vh, 340px);
    border-radius: 32px;
    padding: 7px;

    &::before {
      top: 12px;
      width: 60px;
      height: 18px;
      border-radius: 12px;
    }
  }
`;

export const PhoneScreen = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 30px;
  overflow: hidden;
  background: #000;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  @media (max-width: 860px) {
    border-radius: 25px;
  }
`;

export const VideoBadge = styled.div`
  position: absolute;
  left: 16px;
  bottom: 18px;
  z-index: 2;
  font-family: 'JetBrains Mono', 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  font-size: 10.5px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #f3ede4;
  background: rgba(13,12,10,0.6);
  padding: 6px 9px;
  border-radius: 2px;

  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #d0583c;
    margin-right: 7px;
    vertical-align: 1px;
  }
`;

export const SoundButton = styled.button`
  background: none;
  border: 1px solid #34302a;
  color: #f3ede4;
  font-family: 'Newsreader', Georgia, serif;
  font-size: 14px;
  padding: 11px 18px;
  border-radius: 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: #a89f93;
  }

  &:focus-visible {
    outline: 2px solid #f3ede4;
    outline-offset: 3px;
  }
`;

export const VideoSpec = styled.p`
  font-family: 'JetBrains Mono', 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #7d756b;
  margin: 0;
`;

// Sections below the hero
export const Section = styled(Wrap)`
  padding-block: 80px;
  border-top: 1px solid #34302a;
`;

export const SectionTitle = styled.h2`
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: clamp(36px, 4vw, 48px);
  font-weight: 400;
  color: #f3ede4;
  text-align: center;
  margin: 0 0 56px;

  em {
    font-style: italic;
    color: #d0583c;
  }
`;

export const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const FeatureCard = styled.div`
  padding: 32px;
  background: #23201b;
  border: 1px solid #34302a;
`;

export const FeatureNumber = styled.span`
  display: block;
  font-family: 'JetBrains Mono', 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.16em;
  color: #d0583c;
  margin-bottom: 16px;
`;

export const FeatureTitle = styled.h3`
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: 24px;
  font-weight: 400;
  color: #f3ede4;
  margin: 0 0 12px;
`;

export const FeatureText = styled.p`
  font-family: 'Newsreader', Georgia, serif;
  font-size: 16px;
  line-height: 1.6;
  color: #a89f93;
  margin: 0;
`;

export const DeliverablesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  max-width: 640px;
  margin-inline: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const DeliverableItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  font-family: 'Newsreader', Georgia, serif;
  font-size: 18px;
  line-height: 1.5;
  color: #f3ede4;

  svg {
    flex-shrink: 0;
    margin-top: 4px;
  }
`;

export const WhoText = styled.p`
  font-family: 'Newsreader', Georgia, serif;
  font-size: 22px;
  line-height: 1.5;
  color: #a89f93;
  text-align: center;
  max-width: 36em;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

export const CTASection = styled(Wrap)`
  padding-block: 80px;
  border-top: 1px solid #34302a;
  text-align: center;
`;

export const CTATitle = styled.h2`
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: clamp(32px, 4vw, 44px);
  font-weight: 400;
  color: #f3ede4;
  margin: 0 0 16px;

  em {
    font-style: italic;
    color: #d0583c;
  }
`;

export const CTAText = styled.p`
  font-family: 'Newsreader', Georgia, serif;
  font-size: 18px;
  color: #a89f93;
  margin: 0 0 32px;
`;

export const CTALink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 17px 32px;
  background: transparent;
  border: 1px solid #f3ede4;
  color: #f3ede4;
  font-family: 'IBM Plex Sans', 'Helvetica Neue', Arial, sans-serif;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0.01em;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    background: #f3ede4;
    color: #1a1815;
    gap: 18px;
  }

  &:focus-visible {
    outline: 2px solid #d0583c;
    outline-offset: 3px;
  }
`;

export const FormSection = styled(Wrap)`
  padding-block: 0 80px;
`;

export const FormCard = styled.div`
  max-width: 560px;
  margin: 0 auto;
  background: #23201b;
  border: 1px solid #34302a;
  padding: 48px;

  @media (max-width: 768px) {
    padding: 32px 24px;
  }
`;

export const FormTitle = styled.h2`
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: 32px;
  font-weight: 400;
  color: #f3ede4;
  margin: 0 0 8px;
  text-align: center;
`;

export const FormSubtitle = styled.p`
  font-family: 'Newsreader', Georgia, serif;
  font-size: 16px;
  color: #a89f93;
  text-align: center;
  margin: 0 0 32px;
`;

export const PrivacyNote = styled.p`
  font-family: 'JetBrains Mono', 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  font-size: 11px;
  color: #7d756b;
  text-align: center;
  margin: 16px 0 0;

  a {
    color: #a89f93;
    text-decoration: underline;

    &:hover {
      color: #f3ede4;
    }
  }
`;

export const SuccessCard = styled.div`
  text-align: center;
  padding: 24px;

  h3 {
    font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
    font-size: 28px;
    font-weight: 400;
    color: #f3ede4;
    margin: 0 0 12px;
  }

  p {
    font-family: 'Newsreader', Georgia, serif;
    font-size: 16px;
    color: #a89f93;
    margin: 0;
  }
`;

export const Footer = styled.footer`
  padding: 20px 32px;
  text-align: center;
  flex-shrink: 0;

  span {
    font-family: 'JetBrains Mono', 'IBM Plex Mono', ui-monospace, Menlo, monospace;
    font-size: 12px;
    color: #7d756b;
  }

  a {
    color: #a89f93;
    text-decoration: none;
    margin-left: 16px;

    &:hover {
      color: #f3ede4;
    }
  }
`;

export const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Label = styled.label`
  font-family: 'JetBrains Mono', 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #a89f93;
`;
